import BaseApiService from '../base/BaseApiService';
import { AdminUser, UserFilters, CreateUserRequest, UpdateUserRequest, UserUpdateResponse } from '@app-types/admin';
import { UsersResponse } from '@app-types';

class UserAdminService extends BaseApiService {
  async getAllUsers(filters: UserFilters = {}, page = 1, limit = 50): Promise<UsersResponse> {
    // Handle role_ids array conversion
    const processedFilters = {
      ...filters,
      role_ids: filters.role_ids && filters.role_ids.length > 0 ? filters.role_ids.join(',') : undefined,
      limit,
      offset: (page - 1) * limit
    };

    const params = this.buildQueryParams(processedFilters);
    return this.get<UsersResponse>(`/admin/users?${params}`);
  }

  /**
   * Get all users matching the given filters, ignoring pagination — used for
   * full-dataset export.
   */
  async getAllUsersForExport(filters: UserFilters = {}): Promise<{ total: number; data: AdminUser[] }> {
    const processedFilters = {
      ...filters,
      role_ids: filters.role_ids && filters.role_ids.length > 0 ? filters.role_ids.join(',') : undefined
    };

    const params = this.buildQueryParams(processedFilters);
    return this.get<{ total: number; data: AdminUser[] }>(`/admin/users/export?${params}`);
  }

  async getUser(id: number): Promise<AdminUser> {
    const response = await this.get<{ data: AdminUser }>(`/admin/users/${id}`);
    return response.data;
  }

  async createUser(data: CreateUserRequest): Promise<AdminUser> {
    const response = await this.post<{ data: AdminUser }>('/admin/users', data);
    return response.data;
  }

  async updateUser(id: number, data: UpdateUserRequest): Promise<UserUpdateResponse> {
    const response = await this.put<{ data: UserUpdateResponse }>(`/admin/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: number): Promise<void> {
    await this.delete(`/admin/users/${id}`);
  }

  async importMicrosoftUsers(
    users: { id: string; displayName: string; mail: string }[]
  ): Promise<{ success: number; failed: number }> {
    const response = await this.post<{ result: { success: number; failed: number } }>('/admin/users/microsoft/import', {
      users
    });
    return response.result;
  }

  async importKeycloakUsers(
    users: { id: string; username: string; email?: string; firstName?: string; lastName?: string; enabled: boolean }[]
  ): Promise<{ success: number; failed: number }> {
    const response = await this.post<{ result: { success: number; failed: number } }>('/admin/users/keycloak/import', {
      users
    });
    return response.result;
  }

  async setPassword(id: number, password: string): Promise<void> {
    await this.post(`/admin/users/${id}/set-password`, { password });
  }

  async sendTemporaryPassword(id: number): Promise<void> {
    await this.post(`/admin/users/${id}/send-temporary-password`);
  }
}

const userAdminService = new UserAdminService();
export default userAdminService;
